import { config, getAuthToken } from '../config/config.js';
import { handleApiError } from '../utils/errors.js';

/**
 * API service for testimony endpoints
 */
class TestimonyApiService {
  constructor() {
    this.baseUrl = config.apiBaseUrl + '/testimony';
    this.authUrl = config.apiBaseUrl + '/auth';
  }

  /**
   * Get headers for API requests
   * @param {boolean} includeAuth - Include authorization header
   * @returns {object} - Headers object
   */
  getHeaders(includeAuth = false) {
    const headers = {
      'Content-Type': 'application/json'
    };

    if (includeAuth) {
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Send verification code to user's phone
   * @param {object} data - { firstName, lastName, phone, email? }
   * @returns {Promise<object>} - API response
   */
  async sendVerification(data) {
    try {
      // Backend expects phoneNumber, not phone
      const response = await fetch(`${this.authUrl}/send-code`, {
        method: 'POST',
        headers: this.getHeaders(false), // Don't include auth for initial send
        body: JSON.stringify({
          phoneNumber: data.phone,
          ...(config.isDevelopment && data.email && { email: data.email })
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to send verification code');
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Verify the code sent to user
   * @param {object} data - { phone, code, firstName, lastName, email }
   * @returns {Promise<object>} - API response with token and user data
   */
  async verifyCode(data) {
    try {
      // Backend expects phoneNumber, not phone
      const response = await fetch(`${this.authUrl}/verify-code`, {
        method: 'POST',
        headers: this.getHeaders(false),
        body: JSON.stringify({
          phoneNumber: data.phone,
          code: data.code
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Invalid or expired verification code');
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Create testimony record (requires authentication)
   * @param {object} data - { firstName, lastName, phone, email?, applicantType? }
   * @param {string} token - JWT token from verify-code response
   * @returns {Promise<object>} - API response with testimony ID and videoFileName
   */
  async createTestimony(data, token) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const response = await fetch(`${this.baseUrl}/create`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          ...(data.email && { email: data.email }),
          ...(data.dateOfBirth && { dateOfBirth: data.dateOfBirth }),
          ...(data.applicantType && { applicantType: data.applicantType })
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create testimony record');
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get signed URL for video upload
   * @param {string} testimonyId - Testimony ID
   * @param {string} contentType - Video MIME type
   * @returns {Promise<object>} - { uploadUrl, publicUrl, expiresIn }
   */
  async getUploadUrl(testimonyId, contentType = 'video/webm') {
    try {
      const token = getAuthToken();
      console.log('🔑 Getting upload URL with token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
      
      if (!token) {
        throw new Error('Authentication required. Please refresh and try again.');
      }

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const response = await fetch(`${this.baseUrl}/${testimonyId}/get-upload-url`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ contentType })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to get upload URL');
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Notify backend about video upload
   * @param {string} testimonyId - Testimony ID
   * @param {object} data - { videoUrl, duration, transcription?, metadataJson? }
   * @returns {Promise<object>} - API response
   */
  async notifyUpload(testimonyId, data) {
    try {
      const response = await fetch(`${this.baseUrl}/${testimonyId}/upload`, {
        method: 'PUT',
        headers: this.getHeaders(true),
        body: JSON.stringify({
          videoUrl: data.videoUrl,
          duration: data.duration,
          transcription: data.transcription || null,
          metadataJson: data.metadataJson || {}
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to update testimony with video');
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Notify backend about multi-stage video upload
   * @param {string} testimonyId - Testimony ID
   * @param {object} stageData - { stage, stageName, stageTitle, videoUrl, duration, transcription?, metadataJson? }
   * @returns {Promise<object>} - API response
   */
  async notifyStageUpload(testimonyId, stageData) {
    try {
      const response = await fetch(`${this.baseUrl}/${testimonyId}/upload-stage`, {
        method: 'PUT',
        headers: this.getHeaders(true),
        body: JSON.stringify({
          stage: stageData.stage,
          stageName: stageData.stageName,
          stageTitle: stageData.stageTitle,
          videoUrl: stageData.videoUrl,
          duration: stageData.duration,
          transcription: stageData.transcription || null,
          metadataJson: stageData.metadataJson || {}
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to update testimony with stage video');
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get testimony stages status
   * @param {string} testimonyId - Testimony ID
   * @returns {Promise<object>} - API response with stages array
   */
  async getTestimonyStages(testimonyId) {
    try {
      const response = await fetch(`${this.baseUrl}/${testimonyId}/stages`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to get testimony stages');
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get user's testimonies (requires authentication)
   * @param {string} token - JWT token from verify-code response
   * @returns {Promise<object>} - API response with testimonies array
   */
  async getMyTestimonies(token) {
    try {
      if (!token) {
        throw new Error('Authentication required');
      }

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const response = await fetch(`${this.baseUrl}/my-testimonies`, {
        method: 'GET',
        headers: headers
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to fetch testimonies');
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Resend verification code
   * @param {object} data - { firstName, lastName, phone, email? }
   * @returns {Promise<object>} - API response
   */
  async resendVerification(data) {
    // Same as sendVerification - backend handles resending
    return this.sendVerification(data);
  }

  /**
   * Get a signed download URL for the student's own testimony video
   * @param {string} testimonyId - Testimony ID
   * @param {number} stage - Stage number (1, 2, 3) for multi-stage
   * @param {string} token - JWT token
   * @returns {Promise<object>} - { downloadUrl, filename }
   */
  async getMyDownloadUrl(testimonyId, stage, token) {
    try {
      const authToken = token || getAuthToken();
      if (!authToken) throw new Error('Authentication required');

      const url = new URL(`${this.baseUrl}/${testimonyId}/my-download`);
      if (stage !== undefined && stage !== null) {
        url.searchParams.set('stage', stage);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to get download URL');
      return result.data; // { downloadUrl, filename }
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

// Export singleton instance
export const testimonyApi = new TestimonyApiService();
